import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { isExitTypeAllowedForGender, MALE_ONLY_EXIT_TYPES } from '../exitTypeGender';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const EXIT_TYPES = [
  'voluntary_resignation',
  'retirement',
  'mutual_agreement',
  'termination',
  'contract_end',
  'health_reasons',
  'military_service',
  'woman_marriage_4857_14',
];
export const URGENCIES = ['standard', 'critical_immediate'];
export const STATUSES = [
  'initiated',
  'in_progress',
  'pending_handover_approval',
  'pending_it_zero_access',
  'pending_closure',
  'completed',
  'cancelled',
];

// Gender eligibility for exit reasons lives in a dependency-free module
// (exitTypeGender.js) so it can be unit-tested without the config hook's imports.

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOffboardingsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboardings?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      const payload = {
        employeeId: formData.employeeId,
        managerEmployeeId: formData.managerEmployeeId,
        exitType: formData.exitType,
        urgency: formData.urgency || 'standard',
        exitDate: formData.exitDate,
        lastWorkDay: formData.lastWorkDay || formData.exitDate,
        noticePeriodDays: formData.noticePeriodDays ? parseInt(formData.noticePeriodDays, 10) : null,
        militaryReturnExpectedAt: formData.exitType === 'military_service' ? formData.militaryReturnExpectedAt : null,
        templateId: formData.templateId || null,
      };
      await api.post('/v1/offboardings', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const cancel = useCallback(async (id, reason) => {
    try { await api.post(`/v1/offboardings/${id}/cancel`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const requestHandoverApproval = useCallback(async (id) => {
    try { await api.post(`/v1/offboardings/${id}/request-handover-approval`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const approveHandover = useCallback(async (id) => {
    try { await api.post(`/v1/offboardings/${id}/approve-handover`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const confirmItZeroAccess = useCallback(async (id) => {
    try { await api.post(`/v1/offboardings/${id}/confirm-it-zero-access`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const close = useCallback(async (id, alumniConsentObtained) => {
    try { await api.post(`/v1/offboardings/${id}/close`, { alumniConsentObtained: !!alumniConsentObtained }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const recordMilitaryReturn = useCallback(async (id, returnedAt) => {
    try { await api.post(`/v1/offboardings/${id}/military-return`, { returnedAt }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const exitTypeLabel = (s) => t(`offboarding.exitType.${s}`, { defaultValue: s });
    const urgencyLabel = (s) => t(`offboarding.urgency.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`offboarding.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboarding.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'personEmail',
        label: t('offboarding.email'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personEmail[like]' },
        },
      },
      {
        name: 'exitType',
        label: t('offboarding.exitTypeLabel'),
        options: {
          ...multiSelectFilter(t('offboarding.exitTypeLabel'), EXIT_TYPES, exitTypeLabel),
          customBodyRender: (v) => exitTypeLabel(v),
        },
      },
      {
        name: 'urgency',
        label: t('offboarding.urgencyLabel'),
        options: {
          ...multiSelectFilter(t('offboarding.urgencyLabel'), URGENCIES, urgencyLabel),
          customBodyRender: (v) => urgencyLabel(v),
        },
      },
      {
        name: 'status',
        label: t('offboarding.statusLabel'),
        options: {
          ...multiSelectFilter(t('offboarding.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      dateFilterColumn('initiatedAt', t('offboarding.initiatedAt'), lang),
      dateFilterColumn('exitDate', t('offboarding.exitDate'), lang),
      dateFilterColumn('lastWorkDay', t('offboarding.lastWorkDay'), lang),
      dateFilterColumn('militaryReturnDeadline', t('offboarding.militaryReturnDeadline'), lang),
    ];
  }, [t, lang, data]);

  const employeeAsync = useMemo(() => ({
    onSearch: async (q) => {
      const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: async (id) => {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture };
    },
  }), []);

  // Only someone already working can be offboarded — pre-start people belong
  // to preboarding (cancel that instead). Manager picker stays unrestricted.
  const currentEmployeeAsync = useMemo(() => ({
    onSearch: async (q) => {
      const params = { 'employmentDate[lte]': new Date().toISOString().split('T')[0], pageSize: 20 };
      if (q) params['firstName[like]'] = q;
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: employeeAsync.onGetById,
  }), [employeeAsync]);

  const objectSchema = useMemo(() => {
    const exitTypeOptions = EXIT_TYPES.map((s) => ({ id: s, name: t(`offboarding.exitType.${s}`, { defaultValue: s }) }));
    const urgencyOptions = URGENCIES.map((s) => ({ id: s, name: t(`offboarding.urgency.${s}`, { defaultValue: s }) }));
    return [
      {
        name: 'employeeId', label: t('offboarding.form.employee'), type: 'asyncSelect', grid: 6, required: true,
        ...currentEmployeeAsync,
        // The manager (and gender) are auto-derived on employee selection. `derives`
        // tells the drawer which field to show a loading spinner on while the lookup
        // runs. The manager field is then disabled when the employee HAS an org
        // manager, and cleared+enabled when they don't (see managerEmployeeId below).
        derives: ['managerEmployeeId'],
        deriveFields: async (employeeId) => {
          if (!employeeId) return { managerEmployeeId: null, _hasOrgManager: false, _employeeGender: null };
          const [managerRes, employeeRes] = await Promise.allSettled([
            api.get(`/v1/employees/${employeeId}/manager`),
            api.get(`/v1/employees/${employeeId}`),
          ]);
          const patch = {};
          // Always write managerEmployeeId — set it to the org manager, or null when
          // there is none, so a previously-selected manager never lingers (stale).
          const m = managerRes.status === 'fulfilled' ? managerRes.value?.data?.data : null;
          patch.managerEmployeeId = m?.employeeId || null;
          patch._hasOrgManager = !!m?.employeeId;
          if (employeeRes.status === 'fulfilled') {
            patch._employeeGender = employeeRes.value?.data?.data?.gender ?? null;
          }
          return patch;
        },
      },
      {
        name: 'exitType', label: t('offboarding.form.exitType'), type: 'select', grid: 6, required: true,
        // Gender-specific reasons stay VISIBLE but disabled (with a "yalnızca erkek/kadın"
        // suffix) instead of silently disappearing, so HR understands WHY they can't be
        // picked. The backend still enforces the rule (exitTypeGenderMismatch).
        options: (fd) => exitTypeOptions.map((o) => {
          if (isExitTypeAllowedForGender(o.id, fd?._employeeGender)) return o;
          const suffix = MALE_ONLY_EXIT_TYPES.includes(o.id)
            ? t('offboarding.exitType.maleOnly')
            : t('offboarding.exitType.femaleOnly');
          return { ...o, name: `${o.name} — ${suffix}`, disabled: true };
        }),
        idKey: 'id', labelKey: 'name',
        description: t('offboarding.form.exitTypeGenderHint'),
      },
      { name: 'urgency', label: t('offboarding.form.urgency'), type: 'select', grid: 6, options: urgencyOptions, idKey: 'id', labelKey: 'name' },
      { name: 'exitDate', label: t('offboarding.form.exitDate'), type: 'date', grid: 6, required: true },
      { name: 'lastWorkDay', label: t('offboarding.form.lastWorkDay'), type: 'date', grid: 6, required: true },
      {
        name: 'managerEmployeeId', label: t('offboarding.form.manager'), type: 'asyncSelect', grid: 6, required: true,
        ...employeeAsync,
        // Locked when the employee has an org-chart manager (auto-filled); editable
        // when they don't, so HR can pick one manually.
        disabled: (fd) => fd._hasOrgManager === true,
      },
      { name: 'noticePeriodDays', label: t('offboarding.form.noticePeriodDays'), type: 'number', grid: 6 },
      {
        name: 'militaryReturnExpectedAt',
        label: t('offboarding.form.militaryReturnExpectedAt'),
        type: 'date',
        grid: 6,
        required: true,
        hidden: (fd) => fd.exitType !== 'military_service',
      },
      {
        name: 'templateId', label: t('offboarding.form.template'), type: 'asyncSelect', grid: 6,
        onSearch: async (q) => {
          const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
          const res = await api.get('/v1/offboarding-task-templates', { params });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map((tp) => ({ id: tp.id, name: tp.name }));
        },
        onGetById: async (id) => {
          const res = await api.get(`/v1/offboarding-task-templates/${id}`);
          const tp = res.data?.data || {};
          return { id, name: tp.name || '' };
        },
      },
    ];
  }, [t, employeeAsync, currentEmployeeAsync]);

  return {
    fetching, data, pagination, columns, objectSchema, refresh,
    create, cancel, requestHandoverApproval, approveHandover, confirmItZeroAccess, close, recordMilitaryReturn,
  };
};

export default useOffboardingsApi;
